import React, { useCallback, useEffect, useRef } from 'react';
import { t } from 'ttag';
import { VisuallyHidden } from '~/lib/a11y/VisuallyHidden';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { withTransition } from '~/ui/transition/Transition';
import { Overlay } from '~/ui/overlay/Overlay';
import { OpenThumbsMenuButton } from './OpenThumbsButton';
import { FeedbackPositive } from './feedback/FeedbackPositive';
import { FeedbackNegative } from './feedback/FeedbackNegative';
import { Root } from './Root';
import { ThumbsPages, useThumbsStore } from './Store';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';
import { ThankScreen, THANK_YOU_MESSAGE } from './feedback/ThankScreen';

const SettingsUI = () => {
  const feedbackDialogRef = useRef<HTMLDivElement>(null);
  const feedbackTriggerRef = useRef<HTMLButtonElement>(null);
  const { open, toggle, page, llamaVariant, setLlamaVariant } =
    useThumbsStore();
  const OverlayWithTransition = withTransition(Overlay);
  useOpenCloseAudioEffect(open);
  useFocusTrap(feedbackDialogRef, open, undefined, feedbackTriggerRef);

  const escFunction = useCallback(event => {
    if (event.keyCode === 27) {
      toggle(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);

    return () => {
      document.removeEventListener('keydown', escFunction);
    };
  }, [escFunction]);

  const rootEl = document.getElementById('root');

  useEffect(() => {
    if (rootEl) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rootEl.inert! = open;
    }
  }, [open]);

  useEffect(() => {
    if (rootEl) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      rootEl.inert! = false;
    }
  }, []);

  useEffect(
    () =>
      setLlamaVariant(
        page === ThumbsPages.FeedbackNegative ? 'sad' : 'default'
      ),
    [page]
  );

  return (
    <>
      <OpenThumbsMenuButton ref={feedbackTriggerRef} />
      <OverlayWithTransition
        show={open}
        open={open}
        onClick={() => toggle(false)}
      >
        <div
          ref={feedbackDialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={t`Feedback`}
        >
          <LlamaModal
            variant={llamaVariant}
            horizontal="right"
            vertical="center"
            close={() => toggle(false)}
          >
            {/*
              CAT-1220 / WCAG 4.1.3: live region stays mounted while the dialog
              is open (empty on first paint), then receives the thank-you text
              when ThankScreen becomes active.
            */}
            <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
              {page === ThumbsPages.ThankScreen ? THANK_YOU_MESSAGE() : ''}
            </VisuallyHidden>
            <Root />
            <FeedbackPositive />
            <FeedbackNegative />
            <ThankScreen />
          </LlamaModal>
        </div>
      </OverlayWithTransition>
    </>
  );
};

export const Thumbs = () => (
  <>
    <SettingsUI />
  </>
);
